import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subscription } from 'rxjs';
import { ChatService } from '../../chat.service';
import { avatarHue, initialsOf } from '../../core/avatar';
import { fileErrorText } from '../../core/file-upload';
import { photoPickProblem } from '../../ui/photo-pick';
import { HubRequestError } from '../../core/hub-types';
import { PhotoCropDialogComponent, PhotoCropData } from '../photo-crop-dialog/photo-crop-dialog.component';

/**
 * The person's own profile photo (2026-09-20). One photo per PERSON on the messaging hub, so the same picture shows in
 * the clinical app, MedicAI Connect and the referrals dashboard, wherever chat names them: the conversation list, the
 * people pickers, and beside what they write in a room.
 *
 * The picture is shrunk to a small square in the browser before it goes (chat-core prepareImage), and the hub strips
 * the photo's metadata. No photo means initials, which is what everyone had before this.
 */
@Component({
  selector: 'app-profile-photo',
  templateUrl: './profile-photo.component.html',
  styleUrls: ['./profile-photo.component.scss'],
})
export class ProfilePhotoComponent implements OnInit, OnDestroy {
  /** null while chat has not said who this is (or chat is off here) */
  photo: string | null = null;
  initials = '?';
  hue = 210;
  busy = false;
  /** false when this deployment has no chat: the whole card is hidden rather than offering something that cannot work */
  available: boolean | null = null;
  private readonly sub = new Subscription();

  constructor(private readonly chat: ChatService, private readonly snack: MatSnackBar, private readonly dialog: MatDialog) {}

  ngOnInit(): void {
    void this.chat.ensureStarted().catch(() => undefined);
    this.sub.add(this.chat.enabled$.subscribe((enabled) => (this.available = enabled)));
    this.sub.add(this.chat.store.state$.subscribe((state) => {
      const me = state.me;
      if (!me) return;
      this.initials = initialsOf(me.displayName);
      this.hue = avatarHue(me.principalId);
      // The person's own photo is on their own conversations' member lists; ask the store for the URL.
      const mine = Object.values(state.conversations).flatMap((c) => c.members ?? []).find((m) => m.principalId === me.principalId);
      this.photo = this.chat.store.avatarUrl(me.principalId, this.myRef ?? mine?.avatar ?? null);
    }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** The ref of the photo just set on this device, which the lists may not have caught up with yet. */
  private myRef: string | null = null;

  async pick(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.busy) return;
    const problem = photoPickProblem(file);
    if (problem) {
      this.snack.open(problem, 'OK', { duration: 5000 });
      return;
    }
    // Choose the square first: what the circle shows is what colleagues see.
    const cropped = await firstValueFrom(this.dialog.open<PhotoCropDialogComponent, PhotoCropData, Blob | null>(
      PhotoCropDialogComponent, { width: '380px', maxWidth: '95vw', data: { file, fileName: file.name } },
    ).afterClosed());
    if (!cropped) return;
    this.busy = true;
    try {
      this.myRef = await this.chat.store.setAvatar(cropped, file.name);
      this.photo = this.chat.store.avatarUrl(this.chat.store.snapshot.me?.principalId ?? '', this.myRef);
      this.snack.open('Your photo is set. Colleagues will see it in chat.', 'OK', { duration: 4000 });
    } catch (e) {
      const code = e instanceof HubRequestError ? e.code : 'UPLOAD_FAILED';
      this.snack.open(fileErrorText(code, file.name), 'OK', { duration: 6000 });
    } finally {
      this.busy = false;
    }
  }

  async remove(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.chat.store.clearAvatar();
      this.myRef = null;
      this.photo = null;
    } catch {
      this.snack.open('The photo could not be removed just now.', 'OK', { duration: 5000 });
    } finally {
      this.busy = false;
    }
  }
}
